<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="text-[#414141] font-sans">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">Donor: Prospect Ratio & Gift Pyramid</h3>
            <ol class="list-decimal pl-5 mb-[1.25rem] text-[14px] leading-[22px]">
                <li>Round your total fundraising goal to the largest $500,000.</li>
                <li>
                    Determine your prospect-to-donor ratio. A prospect-to-donor ratio will help your organization understand the number of prospects it needs for every gift it expects to close. Using the maturity chart below, enter the prospect-to-donor ratio your organization will use. For organizations with more mature donor constituencies, the prospect-to-donor ratio is lower. For organizations with less mature donor constituencies, the prospect-to-donor ratio is higher.
                </li>
                <li>Select your prospect-to-donor ratio.</li>

                <table class="border border-[#414141] border-collapse m-6 text-[14px] leading-[22px]">
                    <tbody>
                        <tr class="bg-[#F5F6FAB3] text-[#00423D]">
                            <th class="font-semibold border border-[#414141] p-3 text-center">Maturity level</th>
                            <th class="font-semibold border border-[#414141] p-3 text-center">Common donor characteristics</th>
                            <th class="font-semibold border border-[#414141] p-3 text-center w-[150px]">Prospect-to donor ratio</th>
                        </tr>
                        <tr>
                            <td class="border border-[#414141] text-[#414141] p-3">Low</td>
                            <td class="border border-[#414141] text-[#414141] p-3">
                                <ul class="list-disc pl-5">
                                    <li>Small donor base giving small gifts</li>
                                    <li class="mt-2">Organization does not know many of these donors</li>
                                </ul>
                            </td>
                            <td class="border border-[#414141] text-center p-3">5:1</td>
                        </tr>
                        <tr>
                            <td class="border border-[#414141] p-3">Average</td>
                            <td class="border border-[#414141] p-3">
                                <ul class="list-disc pl-5">
                                    <li>Some mid and large donors</li>
                                    <li class="mt-2">Donors have some engagement</li>
                                    <li class="mt-2">Donor cultivation in progress</li>
                                </ul>
                            </td>
                            <td class="border border-[#414141] text-center p-3">4:1</td>
                        </tr>
                        <tr>
                            <td class="border border-[#414141] p-3">High</td>
                            <td class="border border-[#414141] p-3">
                                <ul class="list-disc pl-5">
                                    <li>Many large donors and long-time recurring donors</li>
                                    <li class="mt-2">Donors know the organization well</li>
                                </ul>
                            </td>
                            <td class="border border-[#414141] text-center p-3">3:1</td>
                        </tr>
                    </tbody>
                </table>
                <li>Enter prospect names in the 4th column.</li>

                <div class="mt-4 md:flex items-center justify-between">
                    <h3 class="font-['Open_Sans'] font-medium text-xl text-[#00423D]">
                        {{ formatToUSD.format(totalFundraisingGoal) }}
                    </h3>
                    <div class="flex items-center gap-4 justify-around mt-6 md:mt-0">
                        <div class="">
                            <input
                                class="hidden peer"
                                type="radio"
                                name="prospect-ratio"
                                value="5"
                                id="5TO1-ratio"
                                v-model="prospectRatio"
                            />
                            <label
                                for="5TO1-ratio"
                                class="border-2 border-[#00423D] rounded-md px-8 py-2 font-medium text-[#00423D] cursor-pointer peer-checked:bg-[#00423D] peer-checked:border-[#00423D] peer-checked:text-white duration-300"
                                >5:1</label
                            >
                        </div>

                        <div class="">
                            <input
                                class="hidden peer"
                                type="radio"
                                name="prospect-ratio"
                                value="4"
                                id="4TO1-ratio"
                                v-model="prospectRatio"
                            />
                            <label
                                for="4TO1-ratio"
                                class="border-2 border-[#00423D] rounded-md px-8 py-2 font-medium text-[#00423D] cursor-pointer peer-checked:bg-[#00423D] peer-checked:border-[#00423D] peer-checked:text-white duration-300"
                                >4:1</label
                            >
                        </div>
                        <div class="">
                            <input
                                class="hidden peer"
                                type="radio"
                                name="prospect-ratio"
                                value="3"
                                id="3TO1-ratio"
                                v-model="prospectRatio"
                            />
                            <label
                                for="3TO1-ratio"
                                class="border-2 border-[#00423D] rounded-md px-8 py-2 font-medium text-[#00423D] cursor-pointer peer-checked:bg-[#00423D] peer-checked:border-[#00423D] peer-checked:text-white duration-300"
                                >3:1</label
                            >
                        </div>
                    </div>
                </div>

                <GiftPyramidTable 
                    :table-data="tableState.data"
                    :total-fundraising-goal="totalFundraisingGoal"
                />

                <div>
                    <div class="flex justify-center mt-3">
                        <button
                            class="px-10 py-1 inline-block rounded-full font-medium text-white bg-[#00423D] hover:bg-[#00423D]/90 duration-300"
                            @click.prevent="exportExcel"
                        >
                            Export to Excel
                        </button>
                    </div>
                </div>
            </ol>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { useStepValidation } from '@/composables/useStepValidation';
import { useAuthStore } from '@/stores/auth';
import { useFetchCache } from '@/composables/fetchCache';
import { useFetchAnswer } from '@/composables/fetchAnswer';
import { usePostAnswer } from '@/composables/postAnswer';
import GiftPyramidTable from '@/components/GiftPyramidTable.vue';
import * as XLSX from 'xlsx';
import global from '@/global';
import formatToDecimal from '@/composables/formatToDecimal';

// Use the validation composable with default settings
useStepValidation();

// State
const authStore = useAuthStore();
const isLoading = ref(true);
const prospectRatio = ref('3');
const totalFundraisingGoal = ref(0);
const prospectNames = ref([]);
const targetAllocation = ref([0, 0, 0, 0, 0, 0]); // Initialize with default values

const tableState = reactive({
    data: []
});

const formatToUSD = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

function roundToNearest500000(number) {
    if (number < 500000) {
        return 500000;
    }
    return Math.ceil(number / 500000) * 500000;
}

function setProperTargetAllocation() {
    if (totalFundraisingGoal.value >= 20000000) {
        targetAllocation.value = [0.5, 0.2, 0.15, 0.07, 0.04, 0.04];
    } else if (totalFundraisingGoal.value >= 17500000) {
        targetAllocation.value = [0.4, 0.12, 0.08, 0.07, 0.07, 0.26];
    } else if (totalFundraisingGoal.value >= 15000000) {
        targetAllocation.value = [0.15, 0.15, 0.125, 0.085, 0.15, 0.34];
    } else if (totalFundraisingGoal.value >= 12500000) {
        targetAllocation.value = [0.15, 0.2, 0.15, 0.1, 0.07, 0.33];
    } else if (totalFundraisingGoal.value >= 10000000) {
        targetAllocation.value = [0.1, 0.15, 0.12, 0.1, 0.04, 0.49];
    } else if (totalFundraisingGoal.value >= 7500000) {
        targetAllocation.value = [0.0, 0.3, 0.2, 0.14, 0.1, 0.26];
    } else if (totalFundraisingGoal.value >= 5000000) {
        targetAllocation.value = [0.0, 0.2, 0.215, 0.16, 0.14, 0.285];
    } else if (totalFundraisingGoal.value >= 2500000) {
        targetAllocation.value = [0.0, 0.22, 0.15, 0.1, 0.15, 0.38];
    } else if (totalFundraisingGoal.value >= 1000000) {
        targetAllocation.value = [0.0, 0.0, 0.2, 0.25, 0.25, 0.3];
    } else if (totalFundraisingGoal.value >= 500000) {
        targetAllocation.value = [0.0, 0.0, 0.0, 0.1, 0.5, 0.4];
    } else {
        // For values less than 500k, use a default allocation
        targetAllocation.value = [0.0, 0.0, 0.0, 0.0, 0.0, 1.0];
    }
}

async function buildTableData(prospectNamesArr) {
    // Make sure prospectNamesArr is always an array
    if (!prospectNamesArr || !Array.isArray(prospectNamesArr)) {
        prospectNamesArr = Array(7).fill({ names: '' });
    }
    
    // Ensure targetAllocation is properly set
    if (!targetAllocation.value || targetAllocation.value.length === 0) {
        setProperTargetAllocation();
    }
    
    // Make sure totalFundraisingGoal has a valid value
    if (!totalFundraisingGoal.value || isNaN(totalFundraisingGoal.value)) {
        totalFundraisingGoal.value = 500000;
    }

    let giftAmounts = [5000000, 1000000, 500000, 250000, 100000, 25000];
    let tempTableData = [];
    let totalPercentage = 0;
    let calculatedTotalFundraisingGoal = 0;

    let targetAllocationValue = 0;
    let calculatedFundraisingAllocationPercentage = 0;
    let numberOfGiftsNeeded = 0;

    try {
        giftAmounts.forEach((ga, idx) => {
            let amountByGiftLevel = 0;
            // Ensure we always have a valid number for TargetAllocationTemp
            let TargetAllocationTemp = (targetAllocation.value && targetAllocation.value[idx] !== undefined) 
                ? targetAllocation.value[idx] 
                : 0;

            if (totalPercentage + TargetAllocationTemp * 100 <= 100) {
                targetAllocationValue = totalFundraisingGoal.value * TargetAllocationTemp;
                numberOfGiftsNeeded = Math.ceil(targetAllocationValue / ga);

                if (numberOfGiftsNeeded * giftAmounts[idx] + calculatedTotalFundraisingGoal >= totalFundraisingGoal.value) {
                    numberOfGiftsNeeded = Math.floor(targetAllocationValue / ga);
                }

                amountByGiftLevel = numberOfGiftsNeeded * ga;
                calculatedFundraisingAllocationPercentage = +((amountByGiftLevel * 100) / totalFundraisingGoal.value).toFixed(1);
                calculatedTotalFundraisingGoal += amountByGiftLevel;
                totalPercentage += calculatedFundraisingAllocationPercentage;
            } else {
                TargetAllocationTemp = (100 - totalPercentage) / 100;
                targetAllocationValue = totalFundraisingGoal.value * TargetAllocationTemp;
                numberOfGiftsNeeded = Math.ceil(targetAllocationValue / ga);

                if (numberOfGiftsNeeded * giftAmounts[idx] + calculatedTotalFundraisingGoal > totalFundraisingGoal.value) {
                    numberOfGiftsNeeded = Math.floor(targetAllocationValue / ga);
                }

                amountByGiftLevel = numberOfGiftsNeeded * ga;
                calculatedFundraisingAllocationPercentage = +((amountByGiftLevel * 100) / totalFundraisingGoal.value).toFixed(1);
                calculatedTotalFundraisingGoal += amountByGiftLevel;
                totalPercentage += calculatedFundraisingAllocationPercentage;
            }

            tempTableData.push({
                giftAmount: ga,
                numberOfGiftsNeeded: numberOfGiftsNeeded,
                numberOfProspectsNeeded: numberOfGiftsNeeded * prospectRatio.value,
                amountByGiftLevel: amountByGiftLevel,
                cumulativeGiftAmount: tempTableData[idx - 1] ? amountByGiftLevel + tempTableData[idx - 1].cumulativeGiftAmount : amountByGiftLevel,
                fundingAllocation: calculatedFundraisingAllocationPercentage,
                prospectNames: {
                    names: ''
                }
            });
        });

        tempTableData.push({
            giftAmount: 'Various (<$25k)',
            numberOfGiftsNeeded: 'Many',
            numberOfProspectsNeeded: 'Many',
            amountByGiftLevel: totalFundraisingGoal.value - calculatedTotalFundraisingGoal,
            cumulativeGiftAmount: totalFundraisingGoal.value,
            fundingAllocation: 0,
            prospectNames: {
                names: ''
            }
        });

        // Safely assign prospectNames 
        for (let i = 0; i < tempTableData.length; i++) {
            if (prospectNamesArr[i]) {
                tempTableData[i].prospectNames = prospectNamesArr[i];
            }
        }

        tableState.data = tempTableData;

        // Save to API
        try {
            const res = await fetch(`${global.API_URL}/gift-pyramid`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${authStore.user.accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    levels: JSON.stringify(tempTableData)
                })
            });
        } catch (err) {
            console.error('Failed to save gift pyramid data:', err);
        }
    } catch (error) {
        console.error('Error building table data:', error);
        // Provide a minimal fallback data structure
        tableState.data = [
            {
                giftAmount: 'Various',
                numberOfGiftsNeeded: 'Many',
                numberOfProspectsNeeded: 'Many',
                amountByGiftLevel: totalFundraisingGoal.value,
                cumulativeGiftAmount: totalFundraisingGoal.value,
                fundingAllocation: 100,
                prospectNames: { names: '' }
            }
        ];
    }
}

// API Integration
const { error: fetchError, fetchCachedData, cachedData } = useFetchCache({
    uri: '/questions/prospect_to_donor_ratio',
    headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`
    }
});

const answerFetch = useFetchAnswer({
    uri: '/prospects',
    headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`
    }
});

const answerPost = usePostAnswer({
    uri: '/prospects',
    headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        'Content-Type': 'application/json'
    }
});

async function saveUserAnswer() {
    answerPost.queryParam.value = '';
    answerPost.requestBody.value = {
        prospectRatio: prospectRatio.value,
        prospectNames: prospectNames.value
    };

    await answerPost.post();
}

async function getUserAnswer() {
    try {
        // Fetch total fundraising goal from the same API as FundingSummaryView
        const res = await fetch(`${global.API_URL}/total_fundraising_goal/total_fundraising_goal_for_3_years`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${authStore.user.accessToken}`,
            },
        });
        
        if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
        }
        
        const data = await res.json();
        totalFundraisingGoal.value = roundToNearest500000(data.final_total_fundraising_goal_for_3_years || 0);
        setProperTargetAllocation();
        
        // Still fetch prospect-specific data from the original endpoint
        answerFetch.queryParam.value = '';
        await answerFetch.fetchData();
        let answer = answerFetch.fetchedData.value;
        
        prospectRatio.value = answer.prospectRatio || '3';
        prospectNames.value = answer.prospectNames || [];
    } catch (error) {
        console.error('Error fetching user data:', error);
        // Set default values if API calls fail
        totalFundraisingGoal.value = 500000;
        prospectRatio.value = '3';
        prospectNames.value = [];
        setProperTargetAllocation();
    }
}

const exportExcel = async () => {
    try {
        const workbook = XLSX.utils.book_new();
        let xlsxData = [];

        // Make sure we have valid data
        if (!tableState.data || !Array.isArray(tableState.data) || tableState.data.length === 0) {
            console.error('No table data to export');
            return;
        }

        tableState.data.map((ele) => {
            xlsxData.push(ele);
        });

        // Add Grand Total row
        xlsxData.push({
            giftAmount: 'Grand Total',
            numberOfGiftsNeeded: tableState.data.reduce((acc, curr) => {
                if (curr.numberOfGiftsNeeded != 'Many') {
                    return acc + parseInt(curr.numberOfGiftsNeeded);
                }
                return acc + 0;
            }, 0),
            numberOfProspectsNeeded: tableState.data.reduce((acc, curr) => {
                if (curr.numberOfProspectsNeeded != 'Many') {
                    return acc + parseInt(curr.numberOfProspectsNeeded);
                }
                return acc + 0;
            }, 0),
            prospectNames: '',
            amountByGiftLevel: totalFundraisingGoal.value,
            cumulativeGiftAmount: totalFundraisingGoal.value,
            fundingAllocation: '100%'
        });

        const orderedXlsxData = xlsxData.map((ele) => ({
            'Gift Amount': ele.giftAmount == 'Grand Total' || ele.giftAmount == 'Various (<$25k)' ? ele.giftAmount : formatToDecimal(ele.giftAmount - 0),
            'Number Of Gifts Needed': ele.numberOfGiftsNeeded,
            'Number Of Prospects Needed': ele.numberOfProspectsNeeded,
            'Prospect Names': '',
            'Amount By GiftLevel': formatToDecimal(ele.amountByGiftLevel - 0),
            'Cumulative Gift Amount': formatToDecimal(ele.cumulativeGiftAmount - 0),
            'Funding Allocation': typeof ele.fundingAllocation === 'string' ? ele.fundingAllocation : `${parseFloat(ele.fundingAllocation).toFixed(2)}%`
        }));

        const worksheet = XLSX.utils.json_to_sheet(orderedXlsxData);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Gift Pyramid');
        XLSX.writeFile(workbook, 'Gift Pyramid.xlsx');

        // Call API to mark download as completed
        try {
            await fetch(`${global.API_URL}/gift_pyramid/increment_download_flag`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${authStore.user.accessToken}`
                }
            });
        } catch (err) {
            console.error('Failed to track download:', err);
        }
    } catch (err) {
        console.error('Failed to export Excel:', err);
    }
};

watch(prospectRatio, (newRatio) => {
    try {
        buildTableData(prospectNames.value);
        saveUserAnswer(); // Save when the ratio changes
    } catch (err) {
        console.error('Error updating table with new prospect ratio:', err);
    }
});

onMounted(async () => {
    try {
        await fetchCachedData();
        await getUserAnswer();
        setProperTargetAllocation();
        buildTableData(prospectNames.value);
    } catch (err) {
        console.error('Error initializing data:', err);
        // Set a default allocation if we can't get the data
        if (totalFundraisingGoal.value <= 0) {
            totalFundraisingGoal.value = 500000;
        }
        setProperTargetAllocation();
        buildTableData([]);
    } finally {
        isLoading.value = false;
    }
});
</script>

<style scoped>
</style> 